import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticleGuide() {
  return (
    <main>
      <article>
        {/* Article Header */}
        <header className="relative w-full h-[420px] md:h-[520px] flex items-end overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20modern%20workspace%20with%20laptop%20showing%20spreadsheet%20data%20on%20warm%20wooden%20desk%20next%20to%20a%20notebook%20and%20coffee%20cup%20with%20sneakers%20and%20neutral%20toned%20clothing%20items%20neatly%20arranged%20nearby%20soft%20natural%20light%20from%20window%20creating%20warm%20amber%20and%20cream%20atmosphere%20editorial%20photography%20with%20clean%20composition&width=1920&height=800&seq=article-hero-guide-2026&orientation=landscape"
            alt="CSSBuy Spreadsheet Guide 2026"
            title="CSSBuy Spreadsheet Guide 2026 - Complete Resource"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/25"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-10 md:pb-14">
            <Link
              to="/"
              className="inline-flex items-center text-white/70 text-sm hover:text-white mb-4 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line mr-1.5"></i>
              Back to Home
            </Link>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              The Ultimate CSSBuy Spreadsheet Guide 2026
            </h1>
            <div className="flex items-center gap-4 mt-4 text-white/60 text-sm">
              <span>Updated: July 2026</span>
              <span>·</span>
              <span>12 min read</span>
              <span>·</span>
              <span>Beginner to Advanced</span>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">

            {/* Introduction */}
            <section>
              <p className="text-lg md:text-xl text-foreground-800 leading-relaxed">
                If you have spent any time in the rep and streetwear community, there is one resource that consistently separates casual browsers from serious shoppers: a well-maintained <strong className="text-foreground-900">CSSBuy spreadsheet</strong>. This guide is your complete resource for understanding what these spreadsheets are, how to use them effectively, and where to find the best ones in 2026.
              </p>
            </section>

            {/* Section 1 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                What Exactly Is a CSSBuy Spreadsheet?
              </h2>
              <p className="mb-4">
                A <strong className="text-foreground-900">CSSBuy spreadsheet</strong> is a curated, structured list of product links — typically hosted on Google Sheets or similar platforms — that contains direct URLs to items available on Chinese e-commerce marketplaces like Taobao, Weidian, and 1688. These spreadsheets are designed to work seamlessly with the CSSBuy agent service, which acts as an intermediary that purchases items on your behalf, stores them in a warehouse, and ships them internationally.
              </p>
              <p className="mb-4">
                Think of a <strong className="text-foreground-900">CSS spreadsheet</strong> as your personal shopping assistant that has already done the hard work of filtering through thousands of listings to surface only the highest-quality products. Instead of spending hours manually searching with Chinese keywords and hoping for the best, you get a pre-vetted collection of links organized by category, brand, price range, and popularity.
              </p>
              <p>
                The best <strong className="text-foreground-900">CSSBuy spreadsheet 2026</strong> collections go beyond simple link lists. They include seller ratings, QC (Quality Control) photo references, size recommendations, material notes, and community feedback scores — essentially everything you need to make an informed purchase decision without speaking a word of Chinese or spending days researching individual sellers.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                Why You Need a CSSBuy Spreadsheet in 2026
              </h2>
              <p className="mb-4">
                The Chinese e-commerce ecosystem is massive — Weidian alone hosts millions of storefronts. Navigating this landscape without guidance is like walking into the world&apos;s largest shopping mall blindfolded. Here is why a <strong className="text-foreground-900">CSSBuy spreadsheet</strong> is essential:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                {[
                  { title: 'Time Savings', desc: 'Skip hours of manual searching. A good spreadsheet condenses weeks of community research into a single organized document.' },
                  { title: 'Quality Assurance', desc: 'Verified sellers with positive track records mean fewer disappointments and returns.' },
                  { title: 'Price Transparency', desc: 'Compare prices across sellers instantly and avoid overpaying for the same item.' },
                  { title: 'Discover New Finds', desc: 'Get exposed to products and brands you would never stumble upon through casual browsing.' },
                ].map((item, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                How to Use a CSSBuy Spreadsheet: Step-by-Step
              </h2>
              <p className="mb-6">
                Using a <strong className="text-foreground-900">CSSBuy spreadsheet</strong> is simpler than most newcomers expect. Follow these steps and you will be placing your first order in under 10 minutes.
              </p>

              <div className="space-y-6">
                {[
                  { step: 1, title: 'Browse and Select a Category', desc: 'Start by navigating through our 14 curated categories — from Shoes and Hoodies to Accessories and Perfumes. Each category links to a dedicated page with spreadsheets specific to that product type. ' },
                  { step: 2, title: 'Open the Spreadsheet', desc: 'Click through to the main store to access the full spreadsheet. You will see columns for product names, prices (in CNY), seller links, size charts, and community ratings. Take a moment to familiarize yourself with the layout before diving into specific items.' },
                  { step: 3, title: 'Copy the Product Link', desc: 'Once you find an item you want, copy the Taobao or Weidian link from the spreadsheet. Most spreadsheets use clickable hyperlinks, so you can simply right-click and copy the link address.' },
                  { step: 4, title: 'Paste into CSSBuy Agent', desc: 'Log into your CSSBuy account, paste the link into the search bar on the homepage, and the agent will display the product details. You can select your size, color, and quantity before adding it to your cart.' },
                  { step: 5, title: 'Review QC Photos and Ship', desc: 'After the agent purchases your item and it arrives at the warehouse, you will receive QC photos for inspection. If everything looks good, you can choose your shipping method and have your items delivered to your doorstep.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-1.5">{item.title}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4 - Comparison */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                Types of CSSBuy Spreadsheets Compared
              </h2>
              <p className="mb-6">
                Not all spreadsheets are created equal. Understanding the different types available will help you choose the right resource for your shopping style and goals. Here is a quick comparison:
              </p>

              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-5 py-4 font-semibold text-foreground-900">Spreadsheet Type</th>
                      <th className="text-left px-5 py-4 font-semibold text-foreground-900">Best For</th>
                      <th className="text-left px-5 py-4 font-semibold text-foreground-900">Update Frequency</th>
                      <th className="text-left px-5 py-4 font-semibold text-foreground-900">Difficulty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[
                      ['Sneaker Spreadsheets', 'Sneakerheads & collectors', 'Weekly', 'Easy'],
                      ['Brand-Specific Spreadsheets', 'Brand loyalists', 'Bi-weekly', 'Easy'],
                      ['Budget Spreadsheets', 'Bargain hunters', 'Weekly', 'Medium'],
                      ['Luxury Spreadsheets', 'High-end shoppers', 'Monthly', 'Medium'],
                      ['Seasonal Spreadsheets', 'Trend followers', 'Per season', 'Easy'],
                      ['Mega Spreadsheets', 'All-around shoppers', 'Weekly', 'Advanced'],
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-background-50 transition-colors duration-150">
                        <td className="px-5 py-3.5 font-medium text-foreground-800">{row[0]}</td>
                        <td className="px-5 py-3.5 text-foreground-600">{row[1]}</td>
                        <td className="px-5 py-3.5 text-foreground-600">{row[2]}</td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            row[3] === 'Easy' ? 'bg-accent-100 text-accent-700' :
                            row[3] === 'Medium' ? 'bg-primary-100 text-primary-700' :
                            'bg-secondary-100 text-secondary-700'
                          }`}>{row[3]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 5 - Pro Tips */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                Expert Tips for Maximizing Your CSSBuy Spreadsheet Experience
              </h2>
              <div className="space-y-4">
                {[
                  'Always check the last updated date on any spreadsheet before relying on it. Links to Chinese marketplaces can go dead quickly, and a spreadsheet that has not been refreshed in months may contain mostly broken links.',
                  'Cross-reference prices between multiple spreadsheets for the same item. Different curators may have different seller relationships or sourcing methods, and prices can vary by 10-30% for identical products.',
                  'Pay attention to size charts and community notes about sizing. Asian sizing runs significantly smaller than US and EU sizing, and blindly ordering your usual size is the most common mistake new spreadsheet users make.',
                  'Join community forums and Reddit discussions (search for "CSSBuy spreadsheet Reddit") to get real user feedback on specific sellers and items before making large purchases.',
                  'Start with small orders to test seller reliability and shipping times before committing to larger hauls. This minimizes risk while you build confidence with the platform.',
                ].map((tip, i) => (
                  <div key={i} className="flex gap-4 bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                      <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 6 - Common Mistakes */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                Common Mistakes to Avoid
              </h2>
              <p className="mb-4">
                Even experienced shoppers make these mistakes when using a <strong className="text-foreground-900">CSSBuy spreadsheet</strong>. Learn from others and skip the frustration.
              </p>
              <ul className="space-y-3 list-disc list-inside text-foreground-600">
                <li><strong className="text-foreground-800">Ignoring seller ratings.</strong> A low price means nothing if the seller has a history of bait-and-switch or shipping the wrong items.</li>
                <li><strong className="text-foreground-800">Skipping QC photos.</strong> Always pay the small fee for detailed QC photos before shipping. It is your last chance to catch defects, wrong sizes, or bait-and-switch attempts.</li>
                <li><strong className="text-foreground-800">Overloading your first haul.</strong> Shipping costs increase with weight and volume. Start with 3-5 items to understand the total cost breakdown before placing larger orders.</li>
                <li><strong className="text-foreground-800">Forgetting about customs.</strong> Research your country&apos;s import duties and thresholds. A great deal can quickly become expensive if you get hit with unexpected customs fees.</li>
                <li><strong className="text-foreground-800">Using outdated spreadsheets.</strong> Always check for the most recent version. Our <Link to="/" className="text-primary-600 hover:text-primary-700 underline">CSSSpreadsheet homepage</Link> is updated weekly with the latest collections.</li>
              </ul>
            </section>

            {/* Section 7 - FAQ */}
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                Frequently Asked Questions About CSSBuy Spreadsheets
              </h2>
              <div className="space-y-4">
                {[
                  { q: 'How do I find the best CSSBuy spreadsheet for sneakers?', a: 'Our Shoes category is the best starting point. It links directly to curated sneaker spreadsheets organized by brand — Nike, Jordan, Yeezy, New Balance, and more. Each spreadsheet is verified weekly for link validity and seller quality.' },
                  { q: 'Are CSSBuy spreadsheets safe to use?', a: 'Yes, when sourced from trusted directories like ours. We manually verify every spreadsheet before listing it and regularly audit for dead or suspicious links. Always exercise standard online shopping precautions — check seller ratings, read community reviews, and start with small orders.' },
                  { q: 'Can I use a CSSBuy spreadsheet without speaking Chinese?', a: 'Absolutely. The CSSBuy agent platform has an English interface, and most spreadsheets include English product descriptions, size conversions, and notes. The spreadsheet format itself uses standardized columns that are easy to navigate regardless of language.' },
                  { q: 'How much can I save using a CSSBuy spreadsheet?', a: 'Savings vary by product category, but it is common to pay 30-70% less than retail for comparable quality items. Budget spreadsheets can yield even higher savings on basics and unbranded items.' },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 transition-colors duration-200">
                        <i className="ri-add-line text-foreground-500 text-xs group-open:hidden"></i>
                        <i className="ri-subtract-line text-primary-600 text-xs hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/60">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
                Start Your CSSBuy Journey Today
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The world of <strong className="text-foreground-900">CSSBuy spreadsheets</strong> opens up access to thousands of products at prices that simply do not exist in traditional retail. Whether you are hunting for the latest sneaker drops, building a streetwear wardrobe, or looking for unique accessories, a quality <strong className="text-foreground-900">CSS spreadsheet</strong> is your most powerful shopping tool.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Bookmark this <Link to="/" className="text-primary-600 hover:text-primary-700 underline">CSSSpreadsheet homepage</Link> and check back weekly for updated collections, new category additions, and fresh finds across all 14 categories. We are committed to maintaining the most comprehensive, accurate, and user-friendly <strong className="text-foreground-900">CSSBuy spreadsheet 2026</strong> directory available anywhere online.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Browse Spreadsheets
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-foreground-200 text-foreground-800 font-semibold text-sm hover:bg-foreground-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line mr-2"></i>
                  Back to Homepage
                </Link>
              </div>
            </section>

          </div>
        </div>

        {/* Schema.org Article markup */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: 'The Ultimate CSSBuy Spreadsheet Guide 2026',
              description: 'A complete guide to finding, using, and maximizing CSSBuy spreadsheets for sneakers, clothing, accessories, and more.',
              datePublished: '2026-07-01',
              dateModified: '2026-07-06',
              author: {
                '@type': 'Organization',
                name: 'CSSSpreadsheet',
              },
              publisher: {
                '@type': 'Organization',
                name: 'CSSSpreadsheet',
                url: siteUrl,
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${siteUrl}/cssbuy-spreadsheet-guide-2026`,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: siteUrl,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'CSSBuy Spreadsheet Guide 2026',
                  item: `${siteUrl}/cssbuy-spreadsheet-guide-2026`,
                },
              ],
            }),
          }}
        />
      </article>
    </main>
  );
}